<script setup lang="ts">
/**
 * Table cell for a display name. Passes `name` through `formatName` and
 * renders the result one step larger than the table's default body size
 * (`--text-tufte-lg`) so names stand out as the row's primary anchor.
 *
 * Supports a default `<slot>` override: callers that need custom inner
 * markup (e.g. a two-line block) can replace the default `{{ formatted }}`
 * output while still inheriting the cell's typography and `maxWidth` style.
 */
import { computed } from 'vue'
import { formatName } from '@/utils/formatName'

/** Props are reactive: when the parent updates :name, this component re-renders with the new value. */
const props = defineProps<{
  name?: string
  /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
  maxWidth?: string
}>()

const formatted = computed(() => formatName(props.name))
</script>

<template>
  <td
    class="name-cell text-tufte-text font-tufte"
    :style="props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <slot>{{ formatted }}</slot>
  </td>
</template>

<style scoped>
/*
 * Names are the primary anchor in each row, so we render them one step
 * larger than the table's default `--text-tufte-sm` body size to give
 * them clear visual priority over secondary numeric/meta cells.
 */
.name-cell {
  font-size: var(--text-tufte-lg);
}
</style>
